import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  fetchTemplates,
  createTemplate,
  updateTemplate,
  deleteTemplate,
  syncTemplates,
  fetchTemplateStats,
  fetchTemplateAnalytics,
} from '../WhatsApp-Marketing/shared/whatsappApi.js';

// Server state for the WhatsApp Templates page, the sequence builder and the
// template metric drawer.

const asMessage = (error, fallback) => error?.message || fallback;

export const loadTemplates = createAsyncThunk(
  'whatsappTemplates/loadTemplates',
  async (_, { rejectWithValue }) => {
    try {
      const { templates } = await fetchTemplates();
      return templates;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Failed to load templates'));
    }
  }
);

export const addTemplate = createAsyncThunk(
  'whatsappTemplates/addTemplate',
  async (data, { rejectWithValue }) => {
    try {
      return await createTemplate(data);
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Failed to create template'));
    }
  }
);

export const editTemplate = createAsyncThunk(
  'whatsappTemplates/editTemplate',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      return await updateTemplate(id, data);
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Failed to update template'));
    }
  }
);

export const removeTemplate = createAsyncThunk(
  'whatsappTemplates/removeTemplate',
  async (id, { rejectWithValue }) => {
    try {
      await deleteTemplate(id);
      return id;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Failed to delete template'));
    }
  }
);

export const syncTemplatesFromMeta = createAsyncThunk(
  'whatsappTemplates/syncTemplatesFromMeta',
  async (_, { rejectWithValue }) => {
    try {
      const { templates } = await syncTemplates();
      return templates;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Failed to sync templates from Meta'));
    }
  }
);

export const loadTemplateStats = createAsyncThunk(
  'whatsappTemplates/loadTemplateStats',
  async (id, { rejectWithValue }) => {
    try {
      return await fetchTemplateStats(id);
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Failed to load template stats'));
    }
  }
);

export const loadTemplateAnalytics = createAsyncThunk(
  'whatsappTemplates/loadTemplateAnalytics',
  async ({ id, eventType, opts }, { rejectWithValue }) => {
    try {
      return await fetchTemplateAnalytics(id, eventType, opts);
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Failed to load template analytics'));
    }
  }
);

const initialState = {
  templates: [],
  loading: true,
  saving: false,
  // Stats are fetched per row, so cache them by template id.
  statsByTemplate: {},
  error: null,
};

const whatsappTemplatesSlice = createSlice({
  name: 'whatsappTemplates',
  initialState,
  reducers: {
    clearTemplatesError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadTemplates.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.loadError = null;
      })
      .addCase(loadTemplates.fulfilled, (state, action) => {
        state.loading = false;
        state.templates = action.payload;
      })
      .addCase(loadTemplates.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        // Only a failed list load: the page shows it instead of an empty list. Save/delete
        // failures are reported where they happen, not as "couldn't load".
        state.loadError = action.payload;
      })

      .addCase(loadTemplateStats.fulfilled, (state, action) => {
        state.statsByTemplate[String(action.meta.arg)] = action.payload;
      })
      .addCase(loadTemplateStats.rejected, (state, action) => {
        state.statsByTemplate[String(action.meta.arg)] = null;
      })

      .addCase(syncTemplatesFromMeta.fulfilled, (state, action) => {
        state.templates = action.payload;
      })
      .addCase(removeTemplate.fulfilled, (state, action) => {
        state.templates = state.templates.filter(
          (t) => t.id !== action.payload && t._id !== action.payload
        );
      });

    // The page refetches after a write, so these only drive the saving flag.
    for (const thunk of [addTemplate, editTemplate]) {
      builder
        .addCase(thunk.pending, (state) => {
          state.saving = true;
          state.error = null;
        })
        .addCase(thunk.fulfilled, (state) => {
          state.saving = false;
        })
        .addCase(thunk.rejected, (state, action) => {
          state.saving = false;
          state.error = action.payload;
        });
    }
  },
});

export const { clearTemplatesError } = whatsappTemplatesSlice.actions;

export const selectTemplateStats = (state, id) =>
  state.whatsappTemplates.statsByTemplate[String(id)] || null;

export default whatsappTemplatesSlice.reducer;
